import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { AuthService } from '../../shared/services/auth.service';
import { AuthLeftPanelComponent } from '../../shared/components/auth-left-panel/auth-left-panel.component';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { UnauthorizedLayoutComponent } from '../../shared/components/unauthorized-layout/unauthorized-layout.component';

interface TokenValidationResponse {
  success: boolean;
  message: string;
  data: {
    isValid: boolean;
    isAlreadyVerified: boolean;
    otpExpiresAt: string;
    message: string;
  };
  errors: string[] | null;
}

interface VerifyEmailRequest {
  verificationToken: string;
  otpCode: string;
  email: string;
}

interface VerifyEmailResponse {
  success: boolean;
  message: string;
  data: {
    userId: number;
    email: string;
    isVerified: boolean;
    message: string;
  };
  errors: string[] | null;
}

@Component({
  selector: 'app-verify-email',
  standalone: true,
  templateUrl: './verify-email.html',
  styleUrls: ['./verify-email.scss'],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    AuthLeftPanelComponent,
    UnauthorizedLayoutComponent
  ]
})
export class VerifyEmailComponent implements OnInit, OnDestroy {
  form: FormGroup;
  isLoading = false;
  isValidatingToken = true;
  errorMessage: string | null = null;
  successMessage: string | null = null;
  tokenMessage: string | null = null;
  verificationToken: string = '';
  isTokenValid = false;
  isAlreadyVerified = false;
  otpExpiresAt: Date | null = null;
  userEmail: string = '';
  verificationMessage: string = '';
  resendCountdown: number = 0;
  isResendDisabled: boolean = false;
  private countdownInterval: any;
  private destroy$ = new Subject<void>();

  constructor(
    private fb: FormBuilder,
    private authService: AuthService,
    private router: Router,
    private route: ActivatedRoute
  ) {
    this.form = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
      otpCode: ['', [Validators.required, Validators.minLength(6), Validators.maxLength(6)]]
    });
  }

  ngOnInit(): void {
    // Support two entry modes: token from query param OR sessionStorage (registration flow)
    this.route.queryParams.pipe(takeUntil(this.destroy$)).subscribe(params => {
      const tokenParam = params['token'] || null;
      const msgParam = params['message'] || null;

      // Gather any session-stored verification data (set by registration flow)
      const sessionToken = sessionStorage.getItem('verificationToken');
      const sessionEmail = sessionStorage.getItem('registrationEmail') || sessionStorage.getItem('registrationUserEmail');
      const sessionMsg = sessionStorage.getItem('otpMessage');
      const sessionExpires = sessionStorage.getItem('otpExpiresAt');

      // Use query param token if present, otherwise use session token
      this.verificationToken = tokenParam || sessionToken || '';

      // Set messages/email from either params or session
      this.verificationMessage = msgParam || sessionMsg || '';
      this.userEmail = sessionEmail || '';
      if (this.userEmail) {
        this.form.patchValue({ email: this.userEmail });
      }

      if (sessionExpires) {
        this.otpExpiresAt = new Date(sessionExpires);
        this.startCountdown();
      }

      if (this.verificationToken) {
        // If token came from query param we should validate it; if it came from session we assume it's valid
        if (tokenParam) {
          this.validateToken();
        } else {
          // session-based flow (e.g., just-registered)
          this.isValidatingToken = false;
          this.isTokenValid = true;
        }
      } else {
        this.tokenMessage = 'Invalid verification link. Token is missing.';
        this.isValidatingToken = false;
      }
    });
  }

  private startCountdown(): void {
    if (!this.otpExpiresAt) return;

    this.countdownInterval = setInterval(() => {
      const now = new Date().getTime();
      const expiresAt = this.otpExpiresAt!.getTime();
      const distance = expiresAt - now;

      if (distance < 0) {
        clearInterval(this.countdownInterval);
        this.isResendDisabled = false;
        this.resendCountdown = 0;
      } else {
        this.resendCountdown = Math.ceil(distance / 1000);
      }
    }, 1000);
  }

  /**
   * Validate the verification token
   */
  private validateToken(): void {
    this.authService.validateVerificationToken(this.verificationToken)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: TokenValidationResponse) => {
          this.isValidatingToken = false;
          
          if (response.success) {
            const data = response.data;
                       
            // Check if already verified
            if (data.isAlreadyVerified) {
              this.isAlreadyVerified = true;
              this.tokenMessage = 'This email has already been verified. You can now log in.';
              setTimeout(() => {
                this.router.navigate(['/login']);
              }, 3000);
              return;
            }

            // Check if token is valid
            if (data.isValid) {
              this.isTokenValid = true;
              if (data.otpExpiresAt) {
                this.otpExpiresAt = new Date(data.otpExpiresAt);
              }
            } else {
              // Token expired or invalid
              this.errorMessage = data.message || 'Token is invalid or has expired.';
              this.tokenMessage = data.message;
            }
          } else {
            this.errorMessage = response.message || 'Token validation failed.';
            this.tokenMessage = response.message;
          }
        },
        error: (error: any) => {
          this.isValidatingToken = false;
          let errorMsg = error?.message || 'Failed to validate token.';
          this.errorMessage = errorMsg;
          this.tokenMessage = errorMsg;
        }
      });
  }

  /**
   * Submit email verification form
   */
  onSubmit(): void {
    if (this.form.invalid || !this.verificationToken) return;

    this.errorMessage = null;
    this.successMessage = null;
    this.isLoading = true;

    const verifyRequest: VerifyEmailRequest = {
      verificationToken: this.verificationToken,
      otpCode: this.form.value.otpCode,
      email: this.form.value.email
    };

    this.authService.verifyEmail(verifyRequest)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: VerifyEmailResponse) => {
          this.isLoading = false;

          if (response.success && response.data.isVerified) {
            this.successMessage = response.data.message || response.message;
            setTimeout(() => {
              this.router.navigate(['/login']);
            }, 3000);
          } else {
            this.errorMessage = response.data.message || response.message || 'Email verification failed.';
          }
        },
        error: (error: any) => {
          this.isLoading = false;
          let errorMsg = error?.message || 'Email verification failed. Please try again.';
          this.errorMessage = errorMsg;
        }
      });
  }

  /**
   * Get error message for field
   */
  getFieldError(fieldName: string): string | null {
    const control = this.form.get(fieldName);
    if (!control || !control.touched) return null;

    if (control.hasError('required')) {
      return `${fieldName.charAt(0).toUpperCase() + fieldName.slice(1)} is required`;
    }

    if (control.hasError('email')) {
      return 'Please enter a valid email address';
    }

    if (control.hasError('minlength')) {
      return `OTP must be at least 6 characters`;
    }

    return null;
  }

  ngOnDestroy(): void {
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
    }
    this.destroy$.next();
    this.destroy$.complete();
  }

  getCountdownDisplay(): string {
    const minutes = Math.floor(this.resendCountdown / 60);
    const seconds = this.resendCountdown % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }
}
